<template>
    <div class="side-function-bar-comments">
        <Comment
            ref="comments"
            :class="{
                'comment-box-dialog__content': true,
                'comment-box-dialog__content--empty': !originalCommentList.length,
                'comment-box-dialog__content--not-editable': !permission.edit
            }"
            :isLoading="commentLoading"
            :currentUser="currentUser"
            :permission="permission"
            :commentList="originalCommentList"
            :maxLength="maxLength"
            :timeZone="timeZone.utc_offset"
            :showLimitInTextarea="true"
            :patentId="patentId"
            :isPersonalAccount="isPersonalAccount"
            :sourceFrom="sourceFrom"
            sourceIn="ANALYTICS"
            @cancel="cancel"
            @submit="submit"
            @delete="remove"
            @selectedOptionChange="selectedOptionChange"
            @wordingClicked="wordingInCommentClicked"
        >
            <template #handler>
                <CommentTextareaHandler />
            </template>
        </Comment>
        <ElDialog
            v-if="showCommentDialog"
            :visible="true"
            :width="'600px'"
            :appendToBody="true"
            :closeOnClickModal="false"
            :closeOnPressEscape="false"
            class="comment-tips-dialog"
            @close="onCloseCommentDialog"
        >
            <div class="comment-tips-dialog__title">
                {{ $t('patent-view.commentList.commentNotFind') }}
            </div>
            <div class="comment-tips-dialog__content">
                {{ commentContent }}
            </div>
        </ElDialog>
    </div>
</template>

<script>
import _ from 'lodash';
import { MessageBox, Dialog } from 'element-ui';
import CommentService from '../../../services/comment-service';
import Comment from '../../../../../../components/components/comments/Main';
import WordingHandler from '../../../utils/wording-handler';
import { flattenComments } from '../../../../../../components/components/comments/scripts/utils';
import { MAX_LIMIT_COMMENT_TEXT_LENGTH } from '../../../constants';
import { getRouteNameList } from '../../../router';
import CommentTextareaHandler from './CommentTextareaHandler.vue';
import { useCommentDialog } from '@/components/dialog/CommentDialog/composables/use-comment-dialog';

export default {
    components: {
        Comment,
        ElDialog: Dialog,
        CommentTextareaHandler,
    },
    props: {
        patentId: {
            type: String,
            required: true,
        },
        spaceId: {
            type: String,
            required: true,
        },
        folderId: {
            type: String,
            required: true,
        },
        maxLength: {
            type: Number,
            default: MAX_LIMIT_COMMENT_TEXT_LENGTH,
        },
        permission: {
            type: Object,
            default() {
                return {
                    edit: true,
                    read: true,
                    isOwner: true,
                };
            },
        },
    },
    setup() {
        const { setIsShowingCommentBox } = useCommentDialog();

        return {
            toggleCommentBox: setIsShowingCommentBox,
        };
    },
    data() {
        return {
            commentLoading: false,
            currentUser: {},
            timeZone: {},
            originalCommentList: [],
            showCommentDialog: false,
            commentContent: '',
        };
    },
    computed: {
        commentLength() {
            return flattenComments(this.originalCommentList).length;
        },
        isPersonalAccount() {
            return !_.get(this, 'currentUser.company_id');
        },
        sourceFrom() {
            return this.spaceId ? '' : 'ANALYTICS';
        },
    },
    watch: {
        commentLength: {
            handler: function(val) {
                this.$emit('commentLengthChange', val);
            },
            immediate: true,
        },
    },
    created() {
        // 监听wording事件
        WordingHandler.$on('clickComment', this.wordingClickedCallback);
        WordingHandler.$on('addNewComment', this.wordingAddCommentCallback);
        WordingHandler.$on('showCommentDialog', this.onShowCommentDialog);
        this.$confirm = MessageBox.confirm;
        this.currentUser = _.get(window, 'zPREFACE.accountInfo');
    },
    mounted() {
        this.commentService = new CommentService(this.patentId, this.spaceId, this.folderId);
        this.getOriginalCommentList();
    },
    beforeDestroy() {
        WordingHandler.$off('clickComment', this.wordingClickedCallback);
        WordingHandler.$off('addNewComment', this.wordingAddCommentCallback);
    },
    methods: {
        async getOriginalCommentList() {
            this.commentLoading = true;
            this.originalCommentList = await this.commentService.getCommentList();
            this.timeZone = await this.commentService.getCommentTimeZone();
            this.commentLoading = false;
        },
        selectedOptionChange(option) {
            this.commentService.setVisibleScope(option);
            this.getOriginalCommentList();
        },
        open() {
            this.toggleCommentBox(true);
        },
        onCloseHandler() {
            this.toggleCommentBox(false);
        },
        deleteComment(comment) {
            this.$confirm(this.$t('patent-view.DeleteCommentConfirm.deleteComment'), {
                customClass: 'patsnap-el-message-box',
                title: this.$t('patent-view.DeleteCommentConfirm.title'),
                confirmButtonText: this.$t('patent-view.DeleteCommentConfirm.delete'),
                cancelButtonText: this.$t('patent-view.DeleteCommentConfirm.cancel'),
                confirmButtonClass: 'patsnap-el-confirm',
                cancelButtonClass: 'patsnap-el-cancel',
            }).then(async () => {
                this.commentLoading = true;
                const { commentList } = await this.commentService.deleteThenGetCommentList(comment.comment_id);
                this.originalCommentList = commentList;
                this.commentLoading = false;
                // 如果是划段的 comment 需要通知外层更新 count
                if (comment.wording_id) {
                    const count = (_.filter(flattenComments(commentList), item => item.wording_id === comment.wording_id) || []).length;
                    WordingHandler.$emit('commentUpdated', { comment, count });
                }
            }).catch(() => {});
        },
        async editComment(params) {
            this.commentLoading = true;
            const commentList = await this.commentService.editThenGetComment(params);
            this.originalCommentList = commentList;
            this.commentLoading = false;
        },
        async createComment(params) {
            const { commentText, commentTo, commentToId, wordingId, visibleScope } = params;
            this.commentLoading = true;
            const { comment_id, commentList, comment } = await this.commentService.createThenGetCommentList(commentText, commentTo, commentToId, wordingId, visibleScope);
            this.originalCommentList = commentList;
            this.commentLoading = false;
            // 如果是划段的 comment 需要通知外层更新 count
            if (wordingId) {
                const count = (_.filter(flattenComments(commentList), item => item.wording_id === wordingId) || []).length;
                WordingHandler.$emit('commentUpdated', { comment: { ...comment, source }, count });
            }
            this.$nextTick(() => {
                this.$refs.comments.scrollToCommentById(comment_id);
            });
        },
        cancel() {
            if (this.commentLength === 0) {
                this.onCloseHandler();
            }
        },
        submit({ type, data }) {
            if (type === 'add') {
                const params = {
                    commentText: data.comment_text,
                    commentTo: data.comment_to,
                    commentToId: data.comment_to_id,
                    wordingId: data.wording_id,
                    source: data.source,
                    visibleScope: data.visible_scope,
                };
                this.createComment(params);
            } else {
                const params = {
                    commentText: data.comment_text,
                    commentId: data.comment_id,
                    visibleScope: data.visible_scope,
                };

                this.editComment(params);
            }
            window.rvTrack('Click Comments', { source: getRouteNameList(this.$route) });
        },
        remove(comment) {
            this.deleteComment(comment);
        },
        // 点击comment定位到对应wording
        wordingInCommentClicked(comment) {
            WordingHandler.$emit('wordingInCommentClicked', comment);
        },
        // 点击wording定位到对应comment
        wordingClickedCallback(wordingId) {
            const ALL = this.$refs.comments.commentTypes[0];
            if (this.$refs.comments.selectedOption.key !== ALL.key) {
                this.$refs.comments.onSelectedOptionChange(ALL);
            }
            if (!wordingId || this.commentLoading || !this.originalCommentList.length) return;
            const scrollToCommentById = _.get(this, '$refs.comments.scrollToCommentById');
            if (!scrollToCommentById) return;
            scrollToCommentById(wordingId, 'wording');
        },
        wordingAddCommentCallback(comment, patentId) {
            if (this.patentId !== patentId) return;
            const commentForUpdate = this.originalCommentList.map(item => ({ ...item }));
            commentForUpdate.push(comment);
            this.originalCommentList = commentForUpdate;
        },
        onShowCommentDialog(content) {
            if (this.showCommentDialog) return;
            this.commentContent = content;
            this.showCommentDialog = true;
        },
        onCloseCommentDialog() {
            this.showCommentDialog = false;
            this.commentContent = '';
        },
    },
};
</script>

<style lang="scss">
$comments-selector-height: 64px;
.side-function-bar-comments {
    position: relative;
    height: 100%;

    &__user {
        position: absolute;
        height: 24px;
        line-height: 24px;
        font-weight: 500;
        left: 28px;
        top: 0px;
        color: rgb(6, 22, 50);
        width: calc(100% - 24px);
        text-align: left;
        white-space:nowrap;
        overflow:hidden;
        text-overflow:ellipsis;
    }

    // 覆盖组件 Comments 中的样式
    .comment.comment-box-dialog__content {
        height: 100%;

        &.comment-box-dialog__content--not-editable {
            .comment-container {
                max-height: 100%;
                height: 100%;
            }
        }

        .comment-container__floor {
            padding: 8px 12px;
            .comment-item--primary {
                padding-left: 0;
                padding-right: 0;
            }
            .comment-item--secondary {
                padding-right: 0;
                padding-left: 24px;
            }
            .comment-item__text {
                // 不一定需要改动
                // padding-left: 0;
                .comment-item__bottom {
                    align-items: flex-start;
                    .comment-item__time {
                        padding-top: 3px;
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                    }
                }
            }
            .comment-item__action {
                .comment-item__action-item {
                    margin-right: 2px;
                    .patsnap-biz-icon {
                        font-size: 20px;
                    }
                }
            }
            .comment-item__header {
                display: flex;
                .comment-item__header-relation {
                    flex: 1;
                    float: inherit; // FIXME 兼容性
                    width: inherit; // FIXME 兼容性
                    .comment-by {
                        max-width: 28%;
                    }
                    .comment-to {
                        max-width: 28%;
                    }
                }
                .comment-item__header-time {
                    width: inherit; // FIXME 兼容性
                    float: inherit; // FIXME 兼容性
                }
            }
        }

        .comment-type-selector {
            width: 100%;
            margin-top: 0px;
            margin-left: 0px;
            padding: 12px 10px;
            .comment-type-select-container {
                .patsnap-biz-icon {
                    fill: $fill-normal
                }
                .dropdown-label {
                    width: 200px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
            }
            &-tip {
                margin-left: -6px;
            }
        }

        .comment-loading {
            height: calc(100% - $comments-selector-height);
        }

        .comment-container {
            max-height: calc(100% - $comments-selector-height);
            padding: 0;
            .comment-item__action {
                .patsnap-ui-icon__ie-hack {
                    display: none;
                }
            }
        }

        &.comment--active {
            .comment-container {
                max-height: calc(100% - 126px - $comments-selector-height);
            }
        }

        .comment-container__no-data {
            padding: 0;
            height: calc(100% - 98px);
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
        }

        &.comment--active {
            .comment-container__no-data {
                max-height: calc(100% - 126px - $comments-selector-height);
            }
        }

        .comment-footer {
            box-shadow: 0px -4px 10px 0px rgba(0, 0, 0, 0.06);
            border-top: none;
            height: $comments-selector-height;
            .comment-footer__container {
                padding-left: 0;
                .comment-footer__avator {
                    position: static;
                    margin-bottom: 8px;
                }
            }
            .comment-textarea {
                .comment-item__bottom {
                    .workspace-dropdown-2-container {
                        .dropdown-select {
                            svg.patsnap-biz-icon{
                                color: $status-info-primary !important;
                            }
                        }
                        .dropdown-label {
                            max-width: 70px;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                        }
                    }

                    .patsnap-ui-button--alternative {
                        display: none;
                    }
                    display: flex;
                    .patsnap-ui-button:last-of-type {
                        margin-right: 8px;
                    }
                }
            }
            &--active {
                .comment-textarea {
                    .comment-item__bottom {
                        .patsnap-ui-button--alternative {
                            display: inline;
                        }
                    }
                }
            }
        }

        .comment-footer--border-top {
            height: $comments-selector-height;
            box-shadow: 0px -4px 10px 0px rgba(0, 0, 0, 0.06);
            border-top: none;
            .comment-footer__avator {
                display: none;
            }
            &.comment-footer--active {
                .comment-textarea .comment-item__bottom .patsnap-ui-button--alternative {
                    display: inline;
                }
                .comment-footer__avator {
                    display: block;
                }
            }
        }
    }
}

.comment-tips-dialog.el-dialog__wrapper {

    .comment-tips-dialog__title {
        font-size: 14px;
        line-height: 28px;
        color: $text-hint;
        word-break: break-word;
    }

    .comment-tips-dialog__content {
        line-height: 20px;
        color: $text-primary;
        margin-top: 32px;
    }

    .el-dialog__body {
        padding-bottom: 32px;
    }
}

.tippy-box {
    max-width: 265px !important;
    .tippy-content {
        .dropdown-option {
            min-width: 240px !important;
            .dropdown-option_label {
                width: 200px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
        }
        .option-selected {
            .dropdown-option_icon {
                .patsnap-biz-icon {
                    color: $brand-hover;
                }
            }
        }
        .dropdown-option-selected {
            .dropdown-option_icon {
                .patsnap-biz-icon {
                    color: $brand-hover;
                }
            }
        }
    }
}
</style>
